import React, { useEffect, useState } from 'react'
import { ChangeTableKeyTemplate } from '@templates'
import {
  HeatmapData,
  HistogramData,
  IGenericObject,
  ScatterplotData,
  SortedData,
  SwarmplotChartData,
  SwarmplotData,
  TableKey,
  TableWithFields,
} from '@types'
import { Card, Spinner } from '@ss/meta-core-components'
import styled from '@emotion/styled'
import { HeatMap, MainTable } from '@components'
import { config } from '../../config'
import useRest from '../../hooks/useRest'
import {
  ComputedCell,
  DefaultHeatMapDatum,
  HeatMapDatum,
  HeatMapSerie,
} from '@nivo/heatmap'
import { generateQuery } from '@utils'
import { FilterIcon } from '../../assets/icons/filterIcon'
import { CorrelationTable } from './CorrelationTable'
import { MoreInfo } from './MoreInfo'
import {
  cleanQueryFields,
  convertResponseToNivoFormat,
  defaultCorrelationQueryParams,
  getPercentileRanges,
  sortCorrelation,
} from './utils'

/**
 * Componente pagina Correlazioni
 * @component
 */
export const Correlation: React.FC = () => {
  //region States
  const [params, SetParams] = useState<TableWithFields>({
    filters: {},
    index: config.dataSet.indexes[0].value,
    keys: [],
  })

  const [selectedCouple, setSelectedCouple] = useState<[string, string]>([
    'float',
    'float',
  ])

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [moreInfoLoading, setMoreinfoLoading] = useState<boolean>(false)

  const [sortedData, setSortedData] = useState<SortedData>()
  const [heatmapData, setHeatmapData] = useState<HeatmapData>([])

  const [bivariateScatterData, setBivariateScatterData] =
    useState<ScatterplotData>()
  const [bivariateHeatmapData, setBivariateHeatmapData] =
    useState<HeatmapData>()
  const [bivariateSwarmplotData, setBivariateSwarmplotData] =
    useState<SwarmplotData>()

  const [barData1, setBarData1] = useState<HistogramData>()
  const [barData2, setBarData2] = useState<HistogramData>()
  //endregion
  //region UseRest
  const { post: getCorrelationData } = useRest<{ data: IGenericObject<any> }>(
    '/correlation'
  )
  const { post: getBivariateData } = useRest<{ data: IGenericObject<any> }>(
    '/stats_bivariate_correlation'
  )
  const { post: getHistogramData } = useRest('/histogram')
  const { post: getSummary } = useRest('/summary_statistics')
  //endregion

  const validCorrelationRequest = () => {
    return (
      params.filters.hasOwnProperty('anno') && params.filters.anno?.value !== ''
    )
  }

  useEffect(() => {
    setHeatmapData([])
    setSortedData(undefined)

    resetMoreInfo()

    if (validCorrelationRequest()) {
      setIsLoading(true)
      getCorrelationData({
        index: params.index,
        fields: cleanQueryFields(params),
        query_data: generateQuery({ params: params.filters }),
        ...defaultCorrelationQueryParams,
      }).then(({ data }) => {
        const nivoData = convertResponseToNivoFormat(data)
        const sortedData = sortCorrelation(data)
        setHeatmapData(nivoData)
        setSortedData(sortedData)
        setIsLoading(false)
      })
    }
  }, [params])

  const resetMoreInfo = () => {
    setBarData1(undefined)
    setBarData2(undefined)
    setBivariateHeatmapData(undefined)
    setBivariateScatterData(undefined)
    setBivariateSwarmplotData(undefined)
  }

  const onCellClick = async (
    cell: ComputedCell<HeatMapDatum>,
    event: MouseEvent
  ) => {
    const a1 = params.keys.find((el) => el.value === cell.serieId)
    const a2 = params.keys.find((el) => el.value === cell.data.x)

    if (a1 && a2) {
      setSelectedCouple([a1.type, a2.type])
      loadExtraInfoData(a1, a2)
    }
  }

  const loadExtraInfoData = (field1: TableKey, field2: TableKey) => {
    resetMoreInfo()
    setMoreinfoLoading(true)
    Promise.all([
      getSummary({
        index: params.index,
        field: field1.value,
        query_data: generateQuery({ params: params.filters }),
      }),
      getSummary({
        index: params.index,
        field: field2.value,
        query_data: generateQuery({ params: params.filters }),
      }),
    ]).then(([SummaryA1, SummaryA2]) => {
      const promises: Promise<any>[] = []

      promises.push(
        getBivariateData({
          index: params.index,
          fields: [field1.value, field2.value],
          n_samples:
            field1.type === 'categorical' && field2.type === 'categorical'
              ? 0
              : 100,
          query_data: generateQuery({
            params: params.filters,
            range: [
              getPercentileRanges(field1.value, SummaryA1),
              getPercentileRanges(field2.value, SummaryA2),
            ],
          }),
        })
      )

      promises.push(getFilteredHistogramDataResponse(field1, SummaryA1))
      promises.push(getFilteredHistogramDataResponse(field2, SummaryA2))

      Promise.all(promises).then(([Bivariate, respH1, respH2]) => {
        if (field1.type === 'float' && field2.type === 'float') {
          setBivariateScatterData(constructBivScatterplotData(Bivariate))
        } else if (
          (field1.type === 'float' && field2.type === 'categorical') ||
          (field1.type === 'categorical' && field2.type === 'float')
        ) {
          setBivariateSwarmplotData(constructBivSwarmplotData(Bivariate))
        } else {
          setBivariateHeatmapData(constructBivHeatmapData(Bivariate))
        }

        setBarData1(constructBivBarData(field1, respH1))
        setBarData2(constructBivBarData(field2, respH2))
        setMoreinfoLoading(false)
      })
    })
  }

  const constructBivBarData = (field: TableKey, response: any) => {
    if (field.type === 'float') {
      return { data: response.data, axisLegend: response.labels }
    } else {
      const mappedData = response.categorical.map((el: any) => {
        return { x: el.key, y: el.doc_count }
      })
      return {
        data: mappedData,
        axisLegend: { x: field.value, y: 'frequency' },
      }
    }
  }

  const constructBivScatterplotData = (Bivariate: any): ScatterplotData => {
    const scatterData = [{ id: 'scatter', data: Bivariate.data.coordinates }]
    const linRegr = Bivariate.data.linear_regression.regr_line_coordinates
    const scatterRegr = linRegr.map((el: any) => [el.x, el.y])

    return { data: scatterData, PDF: scatterRegr }
  }

  const constructBivSwarmplotData = (Bivariate: any): SwarmplotData => {
    const swarmData: SwarmplotChartData = []
    const keys = new Set<string>()

    Bivariate.data.results.forEach((el: any) => {
      const key = Object.keys(el)[0]
      keys.add(key)
      el[key].forEach((item: number) => {
        swarmData.push({
          id: key + ' ' + Math.random().toFixed(4),
          group: key,
          value: item,
          volume: 2,
        })
      })
    })

    return {
      data: swarmData,
      groups: Array.from(keys.values()),
      value: 'value',
    }
  }

  const constructBivHeatmapData = (Bivariate: any): HeatmapData => {
    const rows: string[] = Bivariate.data.row_values
    const columns: string[] = Bivariate.data.column_values
    const data: HeatmapData = []

    rows.forEach((row_field, row_idx) => {
      const rowData: HeatMapSerie<DefaultHeatMapDatum, Record<string, any>> = {
        id: row_field,
        data: [],
      }
      columns.forEach((col_field, col_idx) => {
        rowData.data.push({
          x: col_field,
          y: Bivariate.data.contingency_table[row_idx][col_idx],
        })
      })
      data.push(rowData)
    })
    return data
  }

  const getFilteredHistogramDataResponse = (
    field: TableKey,
    summaryStats: IGenericObject<any>
  ) => {
    if (field.type === 'float') {
      return getHistogramData({
        index: params.index,
        field: field.value,
        query_data: generateQuery({
          params: params.filters,
          range: [getPercentileRanges(field.value, summaryStats)],
        }),
        modifier: 'string',
        bins: 50,
      })
    } else {
      return new Promise((resolve) => {
        resolve(summaryStats)
      })
    }
  }

  return (
    <ChangeTableKeyTemplate setParams={SetParams}>
      <MainTable params={params} selectedColumns={[]} />
      <Card
        width={'100%'}
        titleComponent={
          <TitleContainer>
            <FilterIcon style={{ marginRight: '5px' }} />
            CORRELATION
          </TitleContainer>
        }
      >
        <CorrelationContainer>
          {validCorrelationRequest() ? (
            <>
              <MatrixWrapper data-cy={'correlation-matrix-container'}>
                {isLoading && (
                  <CorrelationLoading>
                    <h3 data-cy={'correlation-loading'}>
                      Updating correlation matrix...
                    </h3>
                  </CorrelationLoading>
                )}
                <HeatMap
                  dataCy={'correlation-chart'}
                  data={heatmapData}
                  dimensions={{ width: '100%', height: '600px' }}
                  squaredBlock
                  customMargin={{ top: 100, right: 0, bottom: 60, left: 0 }}
                  customAxisTop={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: -89.9,
                  }}
                  customAxisBottom={null}
                  customAxisLeft={null}
                  onClick={onCellClick}
                />
              </MatrixWrapper>
              <CorrelationTable
                dataCy={'highest'}
                title={'Highest Correlation'}
                tableData={sortedData?.highest || []}
              />
              <CorrelationTable
                dataCy={'lowest'}
                title={'Highest Anti-correlation'}
                tableData={sortedData?.lowest || []}
              />
            </>
          ) : (
            <Warning data-cy={'correlation-error'}>
              Per avviare l'analisi di correlazione, selezionare un Anno dal
              selettore specifico.
            </Warning>
          )}
        </CorrelationContainer>
        {bivariateScatterData ||
        bivariateHeatmapData ||
        bivariateSwarmplotData ? (
          <MoreInfo
            scatterplotData={bivariateScatterData}
            heatmapData={bivariateHeatmapData}
            swarmplotData={bivariateSwarmplotData}
            barData1={barData1}
            barData2={barData2}
            attribute1Type={selectedCouple[0]}
            attribute2Type={selectedCouple[1]}
          />
        ) : (
          <></>
        )}
        {moreInfoLoading ? (
          <Loading>
            <Loading>
              <Spinner data-cy={`more-info-loading`} />
            </Loading>
            <Loading>LOADING</Loading>
          </Loading>
        ) : (
          <></>
        )}
      </Card>
    </ChangeTableKeyTemplate>
  )
}

//region STYLE
const CorrelationContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding-left: 10px;
  justify-content: space-between;
`

const MatrixWrapper = styled.div`
  width: 25%;
  margin-left: 2%;
  margin-top: 2%;
  max-height: 600px;
`

const CorrelationLoading = styled.div`
  width: 100%;
  text-align: center;
  padding: 5px;
  font-size: 15px;
  font-weight: bold;
  height: 50px;
`
const TitleContainer = styled.span`
  color: white;
  font-size: 17px;
  display: flex;
  align-items: center;
`

const Warning = styled.div`
  width: 100%;
  max-height: 600px;
  text-align: center;
  height: 50px;
  padding: 15px;
`

const Loading = styled.div`
  display: grid;
  place-self: center;
  gap: 10px;
`

//endregion
